import { cloneDeep } from 'lodash-es';
import { PROXY_TYPES, type ProxyType } from '@/constants/proxy';
import { dateFormatInUserTimeZone, formatGroupedNumber } from '@/helpers';
import { AbstractModel } from '@/models/abstractModel';
import type {
    IProxyListRowApi,
    IProxySitesAccountsEntry,
    IProxySpeedInfoApi,
    IProxySpeedSiteEntry,
    IProxyTextCellDisplay,
    ProxySiteLabelFn,
} from '@/types/proxy';

export type { IProxyTextCellDisplay, ProxySiteLabelFn };

export class ProxyItem extends AbstractModel {
    static readonly DATA_ERROR_KEY = 'proxy.dataError';
    private static readonly EMPTY_CELL_DISPLAY: IProxyTextCellDisplay = { text: '', class: '' };
    private static readonly PARSE_ERROR_DISPLAY: IProxyTextCellDisplay = { text: ProxyItem.DATA_ERROR_KEY, class: 'text-error' };

    countAccounts: number = 0;
    countAccountsTotal: number = 0;
    countBetsAbnormal: number = 0;
    countBetsSettled: number = 0;
    countryId: number | null = null;
    deletedAt: string | null = null;
    description: string | null = null;
    id: number = 0;
    ip: string = '';
    isActive: 0 | 1 = 0;
    isAgent: 0 | 1 = 0;
    login: string = '';
    name: string = '';
    originalPassword: string = '';
    password: string = '';
    port: number | null = null;
    proxySiteIds: number[] = [];
    sitesAccountsEntries: IProxySitesAccountsEntry[] = [];
    speedInfoEntries: IProxySpeedSiteEntry[] = [];
    type: ProxyType = 'SOCKS5';

    constructor(data?: IProxyListRowApi) {
        super();
        if (!data) return;
        this.id = data.id;
        this.name = data.name;
        this.description = data.description;
        this.ip = data.ip;
        this.port = data.port;
        this.type = data.type as ProxyType;
        this.login = data.login;
        this.password = data.password;
        this.originalPassword = data.password;
        this.isActive = data.is_active;
        this.isAgent = data.is_agent;
        this.countryId = data.country_id;
        this.countAccounts = data.count_accounts;
        this.countAccountsTotal = data.count_accounts_total;
        this.countBetsAbnormal = data.count_bets_abnormal;
        this.countBetsSettled = data.count_bets_settled;
        this.proxySiteIds = data.proxy_site ?? [];
        this.sitesAccountsEntries = this.parseSitesAccounts(data.sites_accounts);
        this.speedInfoEntries = this.parseSpeedInfo(data.speed_info);
        this.deletedAt = data.deleted_at;
    }

    static createEmpty(): ProxyItem {
        return new ProxyItem();
    }

    private static formatConnectTimeCell(entry: IProxySpeedSiteEntry): IProxyTextCellDisplay {
        if (ProxyItem.isZeroTime(entry.proxy_total_time)) {
            return ProxyItem.PARSE_ERROR_DISPLAY;
        }
        if (ProxyItem.isZeroTime(entry.proxy_connect_time)) {
            return ProxyItem.toTextCell('>0.01');
        }
        return ProxyItem.toTextCell(String(entry.proxy_connect_time));
    }

    private static formatLastCheckCell(entry: IProxySpeedSiteEntry): IProxyTextCellDisplay {
        return ProxyItem.toTextCell(dateFormatInUserTimeZone(entry.last_checked_at, 'dd/MM HH:mm:ss'));
    }

    static formatRealIp(realIP: string | false | null | undefined, unknownLabel: string): string {
        if (realIP === false || realIP == null || realIP === '') {
            return unknownLabel;
        }
        return String(realIP);
    }

    static formatSpeedtestSeconds(value: string | number): string {
        const n = Number(value);
        if (Number.isNaN(n) || n === 0) {
            return '0';
        }
        if (n > 0 && n < 0.01) {
            return '<0.01';
        }
        return n.toFixed(2);
    }

    private static formatTotalTimeCell(entry: IProxySpeedSiteEntry): IProxyTextCellDisplay {
        if (ProxyItem.isZeroTime(entry.proxy_total_time)) {
            return ProxyItem.PARSE_ERROR_DISPLAY;
        }
        return ProxyItem.toTextCell(String(entry.proxy_total_time));
    }

    static hasSpeedtestDirectLine(entry: Pick<IProxySpeedSiteEntry, 'direct_connect_time' | 'direct_total_time'>): boolean {
        return !ProxyItem.isZeroTime(entry.direct_connect_time) || !ProxyItem.isZeroTime(entry.direct_total_time);
    }

    static isSpeedtestProxyFailed(entry: Pick<IProxySpeedSiteEntry, 'proxy_connect_time' | 'proxy_total_time'>): boolean {
        return ProxyItem.isZeroTime(entry.proxy_connect_time) && ProxyItem.isZeroTime(entry.proxy_total_time);
    }

    static isZeroTime(value: string | number): boolean {
        return Number(value) === 0;
    }

    static mapSpeedtestSites(sites: Record<string, IProxySpeedSiteEntry>): Map<number, IProxySpeedSiteEntry> {
        const map = new Map<number, IProxySpeedSiteEntry>();
        for (const entry of Object.values(sites)) {
            map.set(entry.id, entry);
        }
        return map;
    }

    private static matchesBookmakerFilter(siteId: number, filterIds: number[] | null): boolean {
        if (!filterIds?.length) return true;
        return filterIds.includes(siteId);
    }

    private static pickLatestSpeedEntry(entries: IProxySpeedSiteEntry[]): IProxySpeedSiteEntry | null {
        let latest: IProxySpeedSiteEntry | null = null;
        for (const info of entries) {
            if (!latest || latest.last_checked_at < info.last_checked_at) {
                latest = info;
            }
        }
        return latest;
    }

    private static toTextCell(text: string, isError = false): IProxyTextCellDisplay {
        return { text, class: isError ? 'text-error' : '' };
    }

    get abnCountClass(): string {
        return this.countBetsAbnormal > 0 ? 'text-error' : 'text-tbGreen';
    }

    get abnPerc(): number {
        if (this.countBetsSettled === 0) return 0;
        return (this.countBetsAbnormal / this.countBetsSettled) * 100;
    }

    get abnPercClass(): string {
        return this.abnPerc > 0 ? 'text-error' : 'text-tbGreen';
    }

    get abnPercDisplay(): string {
        return `${this.abnPerc.toFixed(2)}%`;
    }

    get accountsCountClass(): string {
        return this.countAccounts === 0 ? 'text-orange font-weight-bold' : 'text-tbGreen';
    }

    get accountsCountDisplay(): string {
        return formatGroupedNumber(this.countAccounts);
    }

    get accountsTotalSuffix(): string {
        return ` (${formatGroupedNumber(this.countAccountsTotal)})`;
    }

    get hostname(): string {
        return `${this.ip}:${this.port}`;
    }

    accountsTooltip(filterIds: number[] | null, siteLabel: ProxySiteLabelFn): string {
        const rows = this.sitesAccountsEntries.filter((row) => ProxyItem.matchesBookmakerFilter(row.site, filterIds));
        if (!rows.length) {
            return '';
        }
        return rows.map((row) => `${siteLabel(row.site)}: ${row.active} (${row.total})`).join('<br>');
    }

    applySpeedFieldsFromApi(data: Pick<IProxyListRowApi, 'speed_info'>): void {
        this.speedInfoEntries = this.parseSpeedInfo(data.speed_info);
    }

    buildSpeedTooltip(filterIds: number[] | null, siteLabel: ProxySiteLabelFn, formatLine: (entry: IProxySpeedSiteEntry) => string): string {
        const filtered = this.getFilteredSpeedEntries(filterIds);
        if (!filtered.length) {
            return '';
        }
        return filtered.map(formatLine).join('<br>');
    }

    override clone<T extends this>(): T {
        const copy = new ProxyItem();
        Object.assign(copy, cloneDeep(this));
        return copy as T;
    }

    connectTimeDisplay(filterIds: number[] | null): IProxyTextCellDisplay {
        return this.getLatestSpeedDisplay(filterIds, ProxyItem.formatConnectTimeCell);
    }

    connectTimeTooltip(filterIds: number[] | null, siteLabel: ProxySiteLabelFn, errorLabel: string): string {
        return this.buildSpeedTooltip(filterIds, siteLabel, (entry) => {
            const code = siteLabel(entry.id);
            if (ProxyItem.isZeroTime(entry.proxy_total_time)) {
                return `${code}: ${errorLabel}`;
            }
            if (ProxyItem.isZeroTime(entry.proxy_connect_time)) {
                return `${code}: >0.01`;
            }
            return `${code}: ${entry.proxy_connect_time}`;
        });
    }

    getFilteredSpeedEntries(filterIds: number[] | null): IProxySpeedSiteEntry[] {
        return this.speedInfoEntries.filter((row) => ProxyItem.matchesBookmakerFilter(row.id, filterIds));
    }

    getGridRowClass(): string {
        return this.isInactive() ? 'proxy-row--inactive' : '';
    }

    getLatestSpeedDisplay(filterIds: number[] | null, format: (entry: IProxySpeedSiteEntry) => IProxyTextCellDisplay): IProxyTextCellDisplay {
        const latest = ProxyItem.pickLatestSpeedEntry(this.getFilteredSpeedEntries(filterIds));
        if (!latest) {
            return ProxyItem.EMPTY_CELL_DISPLAY;
        }
        return format(latest);
    }

    isInactive(): boolean {
        return this.isActive === 0;
    }

    isSoftDeleted(): boolean {
        return this.deletedAt != null;
    }

    lastCheckDisplay(filterIds: number[] | null): IProxyTextCellDisplay {
        return this.getLatestSpeedDisplay(filterIds, ProxyItem.formatLastCheckCell);
    }

    lastCheckTooltip(filterIds: number[] | null, siteLabel: ProxySiteLabelFn): string {
        return this.buildSpeedTooltip(filterIds, siteLabel, (entry) => {
            const code = siteLabel(entry.id);
            return `${code}: ${dateFormatInUserTimeZone(entry.last_checked_at, 'dd/MM HH:mm:ss')}`;
        });
    }

    parseSitesAccounts(raw: string | null): IProxySitesAccountsEntry[] {
        if (!raw) return [];
        return (JSON.parse(raw) as Array<{ site: string | number; active: string | number; total: string | number }>).map((row) => ({
            site: Number(row.site),
            active: Number(row.active),
            total: Number(row.total),
        }));
    }

    parseSpeedInfo(raw: string | null): IProxySpeedSiteEntry[] {
        if (!raw) return [];
        const parsed = JSON.parse(raw) as IProxySpeedInfoApi;
        return Object.values(parsed.sites);
    }

    toApiData(): Record<string, unknown> {
        let password: string | undefined;
        if (this.id > 0) {
            password = this.password === '' || this.password === this.originalPassword ? this.originalPassword : this.password;
        } else {
            password = this.password || undefined;
        }

        return {
            ...(this.id > 0 ? { id: this.id } : {}),
            name: this.name,
            description: this.description,
            type: this.type,
            ip: this.ip,
            port: this.port!,
            login: this.login,
            password,
            country_id: this.countryId,
            proxy_site: this.proxySiteIds.length > 0 ? this.proxySiteIds : null,
            is_active: this.isActive,
            is_agent: this.isAgent,
        };
    }

    totalTimeDisplay(filterIds: number[] | null): IProxyTextCellDisplay {
        return this.getLatestSpeedDisplay(filterIds, ProxyItem.formatTotalTimeCell);
    }

    totalTimeTooltip(filterIds: number[] | null, siteLabel: ProxySiteLabelFn, errorLabel: string): string {
        return this.buildSpeedTooltip(filterIds, siteLabel, (entry) => {
            const code = siteLabel(entry.id);
            if (ProxyItem.isZeroTime(entry.proxy_total_time)) {
                return `${code}: ${errorLabel}`;
            }
            return `${code}: ${entry.proxy_total_time}`;
        });
    }
}

export const proxyTypeItems = PROXY_TYPES.map((value) => ({ value, title: value }));
